/**
 * The queue.
 *
 * Sorted by last activity rather than by how long someone has been waiting, and
 * the wait is stated in words on the row instead. Sorting by wait makes an
 * abandoned tab from an hour ago outrank a person typing right now.
 */
import { Loader2, MessageSquare, UserRound } from 'lucide-react';
import { cn } from '@respectly/ui';
import type { ChatSessionMode, ChatSessionSummaryDto } from '@respectly/shared-types';

/** Colour is spent on one distinction: is a person needed, or already here. */
const MODE_STYLE: Record<ChatSessionMode, { label: string; dot: string; text: string }> = {
  waiting: {
    label: 'Waiting for a person',
    dot: 'bg-amber-500',
    text: 'text-amber-700 dark:text-amber-400',
  },
  agent: {
    label: 'With a colleague',
    dot: 'bg-teal-500',
    text: 'text-teal-700 dark:text-teal-400',
  },
  bot: {
    label: 'Assistant is answering',
    dot: 'bg-slate-300 dark:bg-slate-600',
    text: 'text-slate-500 dark:text-slate-400',
  },
  closed: {
    label: 'Ended',
    dot: 'bg-slate-300 dark:bg-slate-700',
    text: 'text-slate-400 dark:text-slate-500',
  },
};

export function SessionList({
  sessions,
  activeId,
  loading,
  onSelect,
}: {
  sessions: ChatSessionSummaryDto[];
  activeId: string | null;
  loading: boolean;
  onSelect: (id: string) => void;
}) {
  if (loading && sessions.length === 0) {
    return (
      <div className="flex items-center gap-2 px-4 py-6 text-sm text-slate-500 dark:text-slate-400">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading conversations&hellip;
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="px-4 py-10 text-center">
        <MessageSquare className="mx-auto h-6 w-6 text-slate-300 dark:text-slate-600" />
        <p className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-200">
          Nothing in the queue
        </p>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Conversations show up here the moment a visitor starts one.
        </p>
      </div>
    );
  }

  return (
    <ul className="divide-y divide-slate-200 dark:divide-slate-800">
      {sessions.map((session) => (
        <SessionRow
          key={session.id}
          session={session}
          active={session.id === activeId}
          onSelect={() => onSelect(session.id)}
        />
      ))}
    </ul>
  );
}

function SessionRow({
  session,
  active,
  onSelect,
}: {
  session: ChatSessionSummaryDto;
  active: boolean;
  onSelect: () => void;
}) {
  const style = MODE_STYLE[session.mode] ?? MODE_STYLE.bot;
  const who = session.visitorName ?? 'Anonymous visitor';

  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          'w-full px-4 py-3 text-left transition-colors',
          'hover:bg-slate-50 dark:hover:bg-slate-800/50',
          active && 'bg-teal-50 dark:bg-teal-950/30'
        )}
      >
        <div className="flex items-center gap-2">
          <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', style.dot)} aria-hidden />
          <span className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
            {who}
          </span>
          {session.returning && (
            <span
              className="shrink-0 rounded-full bg-slate-100 px-1.5 py-px text-[10px] font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300"
              title="This visitor has chatted before"
            >
              returning
            </span>
          )}
          <span className="ml-auto shrink-0 text-[11px] text-slate-400 dark:text-slate-500">
            {relativeTime(session.lastActivityAt)}
          </span>
        </div>

        <p className="mt-1 truncate text-xs text-slate-500 dark:text-slate-400">
          {session.preview ?? 'No messages yet'}
        </p>

        <div className="mt-1.5 flex items-center gap-2 text-[11px]">
          <span className={style.text}>
            {session.mode === 'waiting' && session.waitingSince
              ? `Waiting ${relativeTime(session.waitingSince, true)}`
              : style.label}
          </span>
          {session.claimedByName && session.mode === 'agent' && (
            <span className="flex items-center gap-1 text-slate-400 dark:text-slate-500">
              <UserRound className="h-3 w-3" />
              {session.claimedByName}
            </span>
          )}
          <span className="ml-auto text-slate-400 dark:text-slate-500">{session.brandName}</span>
        </div>
      </button>
    </li>
  );
}

/** Small enough not to earn a date library. `bare` drops the trailing "ago". */
export function relativeTime(iso: string, bare = false): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  const suffix = bare ? '' : ' ago';
  if (seconds < 45) return bare ? 'a few seconds' : 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m${suffix}`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h${suffix}`;
  return `${Math.round(hours / 24)}d${suffix}`;
}
